import React from 'react';
import { useTranslation } from 'react-i18next';
import { C, getTradePalette } from '../albumColors';
import { stickerCode } from '../albumData';

/**
 * Floating bar at the bottom of the album in TRADE_MATCH mode.
 *
 * Empty state (sin selección):
 *   - Mobile: 1 sola fila — hint "Tocá figuritas" + CTA "Intercambio rápido".
 *   - Tablet+: agrega la leyenda de colores debajo del hint.
 * Con selección: chips de Pides / Ofreces + Enviar / Revisar / X.
 *
 * Estilo: glass blanco con accents primary, en línea con TradeDetailModal y
 * QuickSwapConfirmSheet. Reemplaza al bar dark navy original.
 */
function TradeSummaryBar({ offering, requesting, onReview, onClear, onSend, onQuickFill, theme }) {
  const { t } = useTranslation();
  const total = offering.size + requesting.size;
  const tradePalette = getTradePalette(theme);

  const offeringNums = [...offering].sort((a, b) => a - b);
  const requestingNums = [...requesting].sort((a, b) => a - b);

  const formatList = (nums) => {
    if (nums.length === 0) return '';
    if (nums.length <= 6) return nums.map(n => stickerCode(n)).join(', ');
    return nums.slice(0, 5).map(n => stickerCode(n)).join(', ') + ' +' + (nums.length - 5);
  };

  const canSend = offering.size > 0 && requesting.size > 0;

  const receiveBg   = tradePalette.receive.bg ?? '#F59E0B';
  const receiveText = tradePalette.receive.text ?? '#ffffff';
  const receiveStrong = tradePalette.receive.strong ?? '#B45309';
  const accent      = tradePalette.give.bg ?? theme?.primaryAccent ?? C.primary;
  const accentText  = tradePalette.give.text ?? '#ffffff';
  const accentDim   = tradePalette.give.strong ?? theme?.mutedText ?? C.primaryDim;
  const bodyText    = theme?.summaryBarText ?? theme?.cardBodyText ?? C.onSurface;
  const subtleText  = theme?.summaryBarSubtleText ?? theme?.cardSubtleText ?? bodyText;
  const barBg       = theme?.summaryBarBg     ?? 'var(--md-sys-color-surface-container, #f3f4f6)';
  const barText     = bodyText;
  const barBorder   = theme?.summaryBarBorder ?? 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.10))';
  const barShadow   = theme?.summaryBarShadow ?? '0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)';
  const sendBg      = theme?.summaryBarSendBg ?? receiveBg;
  const dividerBg   = theme?.cardRowBorder  ?? `${C.outlineVariant}44`;
  const reviewBg    = theme?.summaryBarReviewBg ?? accent;
  const reviewText  = theme?.summaryBarReviewText ?? accentText;
  const quickFillBg   = theme?.smartTradeButtonBg ?? theme?.primaryAccent ?? C.primary;
  const quickFillText = theme?.smartTradeButtonText ?? '#ffffff';

  return (
    <div
      className="fixed bottom-20 tablet-sm:bottom-6 left-1/2 -translate-x-1/2 z-50
                 flex items-center gap-2 mobile-md:gap-4
                 px-3 mobile-md:px-5 py-2 mobile-md:py-3 rounded-2xl"
      style={{
        backgroundColor: barBg,
        color: barText,
        width: '92vw',
        maxWidth: '52rem',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: `1px solid ${barBorder}`,
        boxShadow: barShadow,
      }}
      role="region"
      aria-label={t('Constructor de intercambio')}
    >
      <div className="flex items-center gap-2 mobile-md:gap-4 flex-1 min-w-0 overflow-x-auto hide-scrollbar">
      {total === 0 ? (
        <div className="flex items-center gap-2 mobile-md:gap-3 flex-1 min-w-0">
          <span className="material-symbols-outlined text-base shrink-0" style={{ color: accent }}>touch_app</span>
          <div className="text-[11px] leading-snug flex-1 min-w-0">
            <div className="font-bold truncate" style={{ color: bodyText }}>
              {t('Tocá figuritas para construir tu propuesta')}
            </div>
            {/* La leyenda de colores sólo en tablet+; en mobile no entra. */}
            <div className="hidden mobile-md:block" style={{ color: subtleText }}>
              <span className="inline-flex items-center gap-1 mr-3">
                <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: receiveBg }} />
                {t('Pides')}
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm" style={{ backgroundColor: accent }} />
                {t('Ofreces')}
              </span>
            </div>
          </div>
          {onQuickFill && (
            <button
              type="button"
              onClick={onQuickFill}
              className="flex items-center gap-1 px-2.5 mobile-md:px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all shrink-0"
              style={{ backgroundColor: quickFillBg, color: quickFillText, boxShadow: `0 4px 12px -4px ${quickFillBg}` }}
              title={t('Pre-cargar la propuesta óptima')}
              aria-label={t('Intercambio rápido')}
            >
              <span className="material-symbols-outlined text-sm">bolt</span>
              <span className="hidden mobile-md:inline">{t('Intercambio rápido')}</span>
              <span className="mobile-md:hidden">{t('Rápido')}</span>
            </button>
          )}
        </div>
      ) : (
        <>
          {requesting.size > 0 && (
            <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: receiveBg }} />
              <span className="text-[11px]" style={{ color: bodyText }}>
                <span className="font-bold">{t('Pides')} ({requesting.size}):</span>{' '}
                <span style={{ color: receiveStrong }} className="tabular-nums hidden mobile-md:inline">{formatList(requestingNums)}</span>
              </span>
            </div>
          )}

          {offering.size > 0 && requesting.size > 0 && (
            <div className="w-px h-5 shrink-0" style={{ backgroundColor: dividerBg }} />
          )}

          {offering.size > 0 && (
            <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: accent }} />
              <span className="text-[11px]" style={{ color: bodyText }}>
                <span className="font-bold">{t('Ofreces')} ({offering.size}):</span>{' '}
                <span style={{ color: accentDim }} className="tabular-nums hidden mobile-md:inline">{formatList(offeringNums)}</span>
              </span>
            </div>
          )}
        </>
      )}
      </div>

      {total > 0 && <div className="w-px h-5 shrink-0" style={{ backgroundColor: dividerBg }} />}

      {total > 0 && (
      <div className="flex items-center gap-1 mobile-md:gap-2 shrink-0">
        <button
          onClick={canSend ? onSend : undefined}
          disabled={!canSend}
          className="flex items-center gap-1 px-2.5 mobile-md:px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            backgroundColor: sendBg,
            color: receiveText,
            boxShadow: canSend ? `0 4px 12px -4px ${sendBg}` : 'none',
          }}
          title={canSend ? t('Enviar propuesta') : t('Necesitas elegir al menos una figurita de cada lado')}
          aria-label={t('Enviar')}
        >
          <span className="material-symbols-outlined text-sm">send</span>
          <span className="hidden mobile-md:inline">{t('Enviar')}</span>
        </button>
        <button
          onClick={canSend ? onReview : undefined}
          disabled={!canSend}
          className="flex items-center gap-1 px-2.5 mobile-md:px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            backgroundColor: reviewBg,
            color: reviewText,
            boxShadow: canSend ? `0 4px 12px -4px ${reviewBg}` : 'none',
          }}
          title={t('Revisar')}
          aria-label={t('Revisar')}
        >
          <span className="material-symbols-outlined text-sm">rate_review</span>
          <span className="hidden mobile-md:inline">{t('Revisar')}</span>
        </button>
        <button
          onClick={onClear}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all"
          style={{ color: subtleText }}
          onMouseEnter={e => (e.currentTarget.style.color = C.error)}
          onMouseLeave={e => (e.currentTarget.style.color = subtleText)}
          title={t('Vaciar selección')}
          aria-label={t('Vaciar selección')}
        >
          <span className="material-symbols-outlined text-sm">close</span>
        </button>
      </div>
      )}
    </div>
  );
}

export default TradeSummaryBar;
